/**
 * WCAG contrast measurement.
 *
 * `DESIGN.md` §3 states its ratios as measured, not assumed, and §9 requires the accent to be
 * re-measured whenever a tenant changes it. That means the measurement has to exist in the code
 * rather than in a comment: `contrast.test.ts` guards the palette with it, the tenant settings
 * screen (§6.10) will validate an accent with it, and the design gallery prints it beside each
 * token.
 *
 * Formulas: WCAG 2.1 relative luminance and contrast ratio.
 */

import { hexToRgb } from './color';

/** WCAG 2.1 relative luminance. */
export function relativeLuminance(hex: string): number {
  const [r, g, b] = hexToRgb(hex).map((channel) => {
    const s = channel / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });

  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** Contrast ratio between two colours, 1 to 21. Order does not matter. */
export function contrastRatio(a: string, b: string): number {
  const [lighter, darker] = [relativeLuminance(a), relativeLuminance(b)].sort((x, y) => y - x);
  return (lighter + 0.05) / (darker + 0.05);
}

/** The ratio as `DESIGN.md` writes it: one decimal, e.g. `17.4`. */
export function ratioToOneDecimal(a: string, b: string): number {
  return Math.round(contrastRatio(a, b) * 10) / 10;
}

/**
 * The §2 constraint on a tenant accent, as a function rather than a sentence: the accent must be
 * readable on the page **and** carry its foreground on a filled button. §6.10's settings screen
 * refuses a value that fails — "an unreadable primary button is not a branding preference".
 */
export function isUsableAccent(accentHex: string, foregroundHex: string, pageHex: string): boolean {
  return contrastRatio(accentHex, pageHex) >= 4.5 && contrastRatio(foregroundHex, accentHex) >= 4.5;
}
